import { lazy } from 'react'

/**
 * Helper to create lazy-loaded modal components
 * Reduces boilerplate from 5 lines to 1 line per modal
 */
const dialog = (path, name) =>
    lazy(() => import(`./dialogs/${path}`).then((m) => ({ default: m[name] })))

/**
 * Modal enum organized by domain
 * Usage: MODALS.CANVAS.CREATE, MODALS.COMMON.DELETE
 */
export const MODALS = {
    // =========================================================================
    // COMMON - Generic reusable modals (used in 2+ places)
    // =========================================================================
    COMMON: {
        DELETE: 'COMMON_DELETE',
        UPLOAD_IMAGE: 'COMMON_UPLOAD_IMAGE',
        TOUR_ALERT: 'COMMON_TOUR_ALERT',
        SAVE_CHANGES: 'COMMON_SAVE_CHANGES',
        SAVE: 'COMMON_SAVE',
        OPEN: 'COMMON_OPEN',
        GENERATE_INSIGHT: 'COMMON_GENERATE_INSIGHT',
        TIME_RANGE: 'COMMON_TIME_RANGE',
        TEXT_INPUT: 'COMMON_TEXT_INPUT', // Generic text input dialog
        REQUEST_INTEGRATION: 'COMMON_REQUEST_INTEGRATION', // Request new integration
    },

    // =========================================================================
    // CANVAS - Canvas modals
    // =========================================================================
    CANVAS: {
        CREATE: 'CANVAS_CREATE',
        RENAME: 'CANVAS_RENAME',
        SHARE: 'CANVAS_SHARE',
        SELECT_RANGE: 'CANVAS_SELECT_RANGE', // TextEditor range selection
        SLIDES: 'CANVAS_SLIDES', // Google Slides upload progress
        EDIT_ELEMENT: 'CANVAS_EDIT_ELEMENT', // Edit element wrapper styles
        ADD_VIDEO: 'CANVAS_ADD_VIDEO', // Add YouTube/Loom video URL
    },

    // =========================================================================
    // SOURCE - Source/Dataset modals
    // =========================================================================
    SOURCE: {
        CREATE_DATASET: 'SOURCE_CREATE_DATASET',
        CALCULATED_COLUMNS: 'SOURCE_CALCULATED_COLUMNS',
        CONFIGURE_SLACK: 'SOURCE_CONFIGURE_SLACK',
        DISCONNECT_SLACK: 'SOURCE_DISCONNECT_SLACK',
        CONNECT_SLACK: 'SOURCE_CONNECT_SLACK',
        API_CONNECTIONS: 'SOURCE_API_CONNECTIONS',
        CONFIGURE: 'SOURCE_CONFIGURE',
        LOAD_SHEETLET: 'SOURCE_LOAD_SHEETLET',
        COMPARE_TABLES: 'SOURCE_COMPARE_TABLES',
        REFRESH_OPTIONS: 'SOURCE_REFRESH_OPTIONS',
        RAW_TABLE_DATA: 'SOURCE_RAW_TABLE_DATA',
        RENAME_TABLE: 'SOURCE_RENAME_TABLE',
        PROPERTIES: 'SOURCE_PROPERTIES',
    },

    // =========================================================================
    // EXPLORER - Explorer modals
    // =========================================================================
    EXPLORER: {
        NEW_METRIC: 'EXPLORER_NEW_METRIC',
        ADD_FILTER: 'EXPLORER_ADD_FILTER',
        SAVED_FILTER: 'EXPLORER_SAVED_FILTER',
        SHEET_SOURCE: 'EXPLORER_SHEET_SOURCE',
        FILTER: 'EXPLORER_FILTER',
        AGGREGATION: 'EXPLORER_AGGREGATION',
        FORMAT: 'EXPLORER_FORMAT',
        PERIOD: 'EXPLORER_PERIOD',
        RESET_INSIGHT: 'EXPLORER_RESET_INSIGHT',
        SAVED_FILTER_INFO: 'EXPLORER_SAVED_FILTER_INFO',
        CREATE_THEME: 'EXPLORER_CREATE_THEME',
        PICTORIAL_SELECT: 'EXPLORER_PICTORIAL_SELECT',
        EDIT_SAVED_FILTER: 'EXPLORER_EDIT_SAVED_FILTER',
    },

    // =========================================================================
    // LIVE_QUERY - LiveQuery modals
    // =========================================================================
    LIVE_QUERY: {
        CREATE_WIZARD: 'LIVE_QUERY_CREATE_WIZARD',
    },

    // =========================================================================
    // LIVE_WORKSHEETS - Live Worksheets modals
    // =========================================================================
    LIVE_WORKSHEETS: {
        CREATE: 'LIVE_WORKSHEETS_CREATE',
    },

    // =========================================================================
    // PROCESS - Process/Diagrams modals
    // =========================================================================
    PROCESS: {
        LOAD_DIAGRAM: 'PROCESS_LOAD_DIAGRAM',
        FILTER_SELECTION: 'PROCESS_FILTER_SELECTION',
    },

    // =========================================================================
    // ACCOUNT - Account/Settings/Workspace modals
    // =========================================================================
    ACCOUNT: {
        INVITE_MEMBER: 'ACCOUNT_INVITE_MEMBER',
        CREATE_WORKSPACE: 'ACCOUNT_CREATE_WORKSPACE',
        EDIT_WORKSPACE: 'ACCOUNT_EDIT_WORKSPACE',
        DELETE_WORKSPACE: 'ACCOUNT_DELETE_WORKSPACE',
    },

    // =========================================================================
    // CHAT - SocketChat modals
    // =========================================================================
    CHAT: {
        FEEDBACK: 'CHAT_FEEDBACK',
    },

    // =========================================================================
    // INSIGHTS - ML/Analytics modals
    // =========================================================================
    INSIGHTS: {
        SAVE_ML_MODEL: 'INSIGHTS_SAVE_ML_MODEL',
    },

    // =========================================================================
    // ONBOARDING - Onboarding flow modals
    // =========================================================================
    ONBOARDING: {
        QUESTION: 'ONBOARDING_QUESTION',
        COMPLETE: 'ONBOARDING_COMPLETE',
    },
}

/**
 * Lazy-loaded modal components organized by domain
 * Components are loaded on-demand when first opened
 */
export const modalComponents = {
    // =========================================================================
    // COMMON - Generic reusable modals
    // =========================================================================
    [MODALS.COMMON.DELETE]: dialog('common/DeleteDialog/DeleteDialog', 'DeleteDialog'),
    [MODALS.COMMON.SAVE_CHANGES]: dialog(
        'common/SaveChangesDialog/SaveChangesDialog',
        'SaveChangesDialog'
    ),
    [MODALS.COMMON.TOUR_ALERT]: dialog('common/TourAlertDialog/TourAlertDialog', 'TourAlertDialog'),
    [MODALS.COMMON.UPLOAD_IMAGE]: dialog(
        'common/UploadImageDialog/UploadImageDialog',
        'UploadImageDialog'
    ),
    [MODALS.COMMON.SAVE]: dialog('common/SaveDialog/SaveDialog', 'SaveDialog'),
    [MODALS.COMMON.OPEN]: dialog('common/OpenDialog/OpenDialog', 'OpenDialog'),
    [MODALS.COMMON.GENERATE_INSIGHT]: dialog(
        'common/GenerateInsightDialog/GenerateInsightDialog',
        'GenerateInsightDialog'
    ),
    [MODALS.COMMON.TIME_RANGE]: dialog('common/TimeRangeDialog/TimeRangeDialog', 'TimeRangeDialog'),
    [MODALS.COMMON.TEXT_INPUT]: dialog('common/TextInputDialog/TextInputDialog', 'TextInputDialog'),
    [MODALS.COMMON.REQUEST_INTEGRATION]: dialog(
        'common/IntegrationRequestDialog/IntegrationRequestDialog',
        'IntegrationRequestDialog'
    ),

    // =========================================================================
    // CANVAS - Canvas modals
    // =========================================================================
    [MODALS.CANVAS.CREATE]: dialog(
        'canvas/CreateCanvasDialog/CreateCanvasDialog',
        'CreateCanvasDialog'
    ),
    [MODALS.CANVAS.RENAME]: dialog(
        'canvas/RenameCanvasDialog/RenameCanvasDialog',
        'RenameCanvasDialog'
    ),
    [MODALS.CANVAS.SHARE]: dialog('canvas/ShareDialog/ShareDialog', 'ShareDialog'),
    [MODALS.CANVAS.SELECT_RANGE]: dialog(
        'canvas/SelectRangeDialog/SelectRangeDialog',
        'SelectRangeDialog'
    ),
    [MODALS.CANVAS.SLIDES]: dialog('canvas/SlidesDialog/SlidesDialog', 'SlidesDialog'),
    [MODALS.CANVAS.EDIT_ELEMENT]: dialog('canvas/EditMenuDialog/EditMenuDialog', 'EditMenuDialog'),
    [MODALS.CANVAS.ADD_VIDEO]: dialog('canvas/AddVideoDialog/AddVideoDialog', 'AddVideoDialog'),

    // =========================================================================
    // ACCOUNT - Account/Settings/Workspace modals
    // =========================================================================
    [MODALS.ACCOUNT.CREATE_WORKSPACE]: dialog(
        'account/CreateEditWorkspaceDialog/CreateEditWorkspaceDialog',
        'CreateEditWorkspaceDialog'
    ),
    [MODALS.ACCOUNT.EDIT_WORKSPACE]: dialog(
        'account/CreateEditWorkspaceDialog/CreateEditWorkspaceDialog',
        'CreateEditWorkspaceDialog'
    ),
    [MODALS.ACCOUNT.DELETE_WORKSPACE]: dialog(
        'account/DeleteWorkspaceDialog/DeleteWorkspaceDialog',
        'DeleteWorkspaceDialog'
    ),

    // =========================================================================
    // EXPLORER - Explorer modals
    // =========================================================================
    [MODALS.EXPLORER.NEW_METRIC]: dialog(
        'explorer/NewMetricDialog/NewMetricDialog',
        'NewMetricDialog'
    ),
    [MODALS.EXPLORER.ADD_FILTER]: dialog(
        'explorer/AddFilterModal/AddFilterModal',
        'AddFilterModal'
    ),
    [MODALS.EXPLORER.SAVED_FILTER]: dialog(
        'explorer/SavedFilterDialog/SavedFilterDialog',
        'SavedFilterDialog'
    ),
    [MODALS.EXPLORER.SHEET_SOURCE]: dialog(
        'explorer/SheetSourceDialog/SheetSourceDialog',
        'SheetSourceDialog'
    ),
    [MODALS.EXPLORER.FILTER]: dialog('explorer/FiltersDialog/FiltersDialog', 'FiltersDialog'),
    [MODALS.EXPLORER.AGGREGATION]: dialog(
        'explorer/AggregationDialog/AggregationDialog',
        'AggregationDialog'
    ),
    [MODALS.EXPLORER.FORMAT]: dialog('explorer/FormatDialog/FormatDialog', 'FormatDialog'),
    [MODALS.EXPLORER.PERIOD]: dialog('explorer/PeriodDialog/PeriodDialog', 'PeriodDialog'),
    [MODALS.EXPLORER.RESET_INSIGHT]: dialog(
        'explorer/ResetInsightDialog/ResetInsightDialog',
        'ResetInsightDialog'
    ),
    [MODALS.EXPLORER.SAVED_FILTER_INFO]: dialog(
        'explorer/SavedFilterInfoDialog/SavedFilterInfoDialog',
        'SavedFilterInfoDialog'
    ),
    [MODALS.EXPLORER.CREATE_THEME]: dialog(
        'explorer/CreateThemeDialog/CreateThemeDialog',
        'CreateThemeDialog'
    ),
    [MODALS.EXPLORER.PICTORIAL_SELECT]: dialog(
        'explorer/PictorialSelectDialog/PictorialSelectDialog',
        'PictorialSelectDialog'
    ),
    [MODALS.EXPLORER.EDIT_SAVED_FILTER]: dialog(
        'explorer/EditSavedFilterDialog/EditSavedFilterDialog',
        'EditSavedFilterDialog'
    ),

    // =========================================================================
    // LIVE_QUERY - LiveQuery modals
    // =========================================================================
    [MODALS.LIVE_QUERY.CREATE_WIZARD]: dialog(
        'live-query/CreateLiveQueryWizard/CreateLiveQueryWizard',
        'CreateLiveQueryWizard'
    ),

    // =========================================================================
    // LIVE_WORKSHEETS - Live Worksheets modals
    // =========================================================================
    [MODALS.LIVE_WORKSHEETS.CREATE]: dialog(
        'live-worksheets/CreateWorksheetDialog/CreateWorksheetDialog',
        'CreateWorksheetDialog'
    ),

    // =========================================================================
    // SOURCE - Source/Dataset modals
    // =========================================================================
    [MODALS.SOURCE.CREATE_DATASET]: dialog(
        'source/CreateDatasetModal/CreateDatasetModal',
        'CreateDatasetModal'
    ),
    [MODALS.SOURCE.CALCULATED_COLUMNS]: dialog(
        'source/CalculatedColumnsDialog/CalculatedColumnsDialog',
        'CalculatedColumnsDialog'
    ),
    [MODALS.SOURCE.CONFIGURE_SLACK]: dialog(
        'source/ConfigureSlackSharingDialog/ConfigureSlackSharingDialog',
        'ConfigureSlackSharingDialog'
    ),
    [MODALS.SOURCE.DISCONNECT_SLACK]: dialog(
        'source/DisconnectSlackDialog/DisconnectSlackDialog',
        'DisconnectSlackDialog'
    ),
    [MODALS.SOURCE.CONNECT_SLACK]: dialog(
        'source/ConnectSlackDialog/ConnectSlackDialog',
        'ConnectSlackDialog'
    ),
    [MODALS.SOURCE.API_CONNECTIONS]: dialog(
        'source/ApiConnectionsDialog/ApiConnectionsDialog',
        'ApiConnectionsDialog'
    ),
    [MODALS.SOURCE.CONFIGURE]: dialog('source/ConfigDialog/ConfigDialog', 'ConfigDialog'),
    [MODALS.SOURCE.LOAD_SHEETLET]: dialog(
        'source/LoadSheetletDialog/LoadSheetletDialog',
        'LoadSheetletDialog'
    ),
    [MODALS.SOURCE.COMPARE_TABLES]: dialog(
        'source/CompareTablesDialog/CompareTablesDialog',
        'CompareTablesDialog'
    ),
    [MODALS.SOURCE.REFRESH_OPTIONS]: dialog(
        'source/RefreshOptionsDialog/RefreshOptionsDialog',
        'RefreshOptionsDialog'
    ),
    [MODALS.SOURCE.RAW_TABLE_DATA]: dialog(
        'source/RawTableDataDialog/RawTableDataDialog',
        'RawTableDataDialog'
    ),
    [MODALS.SOURCE.RENAME_TABLE]: dialog(
        'source/RenameTableDialog/RenameTableDialog',
        'RenameTableDialog'
    ),
    [MODALS.SOURCE.PROPERTIES]: dialog(
        'source/PropertiesDialog/PropertiesDialog',
        'PropertiesDialog'
    ),

    // =========================================================================
    // INSIGHTS - ML/Analytics modals
    // =========================================================================
    [MODALS.INSIGHTS.SAVE_ML_MODEL]: dialog(
        'insights/SaveMLModelDialog/SaveMLModelDialog',
        'SaveMLModelDialog'
    ),

    // =========================================================================
    // CHAT - SocketChat modals
    // =========================================================================
    [MODALS.CHAT.FEEDBACK]: dialog('chat/FeedbackDialog/FeedbackDialog', 'FeedbackDialog'),

    // =========================================================================
    // ACCOUNT - Account/Settings modals
    // =========================================================================
    [MODALS.ACCOUNT.INVITE_MEMBER]: dialog(
        'account/InviteTeamMemberDialog/InviteTeamMemberDialog',
        'InviteTeamMemberDialog'
    ),

    // =========================================================================
    // PROCESS - Process/Diagrams modals
    // =========================================================================
    [MODALS.PROCESS.LOAD_DIAGRAM]: dialog(
        'process/LoadDiagramDialog/LoadDiagramDialog',
        'LoadDiagramDialog'
    ),
    [MODALS.PROCESS.FILTER_SELECTION]: dialog(
        'process/FilterSelectionDialog/FilterSelectionDialog',
        'FilterSelectionDialog'
    ),

    // =========================================================================
    // ONBOARDING - Onboarding flow modals
    // =========================================================================
    [MODALS.ONBOARDING.QUESTION]: dialog(
        'onboarding/OnboardingQuestionDialog/OnboardingQuestionDialog',
        'OnboardingQuestionDialog'
    ),
    [MODALS.ONBOARDING.COMPLETE]: dialog(
        'onboarding/OnboardingCompleteDialog/OnboardingCompleteDialog',
        'OnboardingCompleteDialog'
    ),
}

/**
 * Default props per modal type (optional)
 * Used to set consistent defaults across the app
 */
export const modalDefaults = {
    [MODALS.COMMON.DELETE]: { maxWidth: 'sm' },
    [MODALS.CANVAS.CREATE]: { maxWidth: 'md' },
    [MODALS.CANVAS.SHARE]: { maxWidth: 'md' },
    [MODALS.COMMON.SAVE]: { maxWidth: 'md' },
    [MODALS.COMMON.OPEN]: { maxWidth: 'md' },
    [MODALS.COMMON.GENERATE_INSIGHT]: { maxWidth: 'md' },
    [MODALS.COMMON.TIME_RANGE]: { maxWidth: 'sm' },
    [MODALS.EXPLORER.NEW_METRIC]: { maxWidth: 'lg' },
    [MODALS.EXPLORER.ADD_FILTER]: { maxWidth: 'md' },
    [MODALS.EXPLORER.SAVED_FILTER]: { maxWidth: 'md' },
    [MODALS.EXPLORER.SHEET_SOURCE]: { maxWidth: 'md' },
    [MODALS.LIVE_QUERY.CREATE_WIZARD]: { maxWidth: 'lg' },
    [MODALS.EXPLORER.FILTER]: { maxWidth: 'md' },
    [MODALS.EXPLORER.AGGREGATION]: { maxWidth: 'sm' },
    [MODALS.EXPLORER.FORMAT]: { maxWidth: 'sm' },
    [MODALS.EXPLORER.PERIOD]: { maxWidth: 'sm' },
    [MODALS.EXPLORER.RESET_INSIGHT]: { maxWidth: 'sm' },
    [MODALS.EXPLORER.SAVED_FILTER_INFO]: { maxWidth: 'sm' },
    [MODALS.EXPLORER.CREATE_THEME]: { maxWidth: 'md' },
    [MODALS.EXPLORER.PICTORIAL_SELECT]: { maxWidth: 'lg' },
    [MODALS.EXPLORER.EDIT_SAVED_FILTER]: { maxWidth: 'lg' },
    [MODALS.SOURCE.CALCULATED_COLUMNS]: { maxWidth: 'lg' },
    [MODALS.SOURCE.CONFIGURE]: { maxWidth: 'md' },
    [MODALS.SOURCE.LOAD_SHEETLET]: { maxWidth: 'md' },
    [MODALS.SOURCE.CONNECT_SLACK]: { maxWidth: 'lg' },
    [MODALS.SOURCE.DISCONNECT_SLACK]: { maxWidth: 'sm' },
    [MODALS.SOURCE.API_CONNECTIONS]: { maxWidth: 'md' },
    [MODALS.SOURCE.CONFIGURE_SLACK]: { maxWidth: 'sm' },
    [MODALS.SOURCE.COMPARE_TABLES]: { maxWidth: 'lg' },
    [MODALS.SOURCE.REFRESH_OPTIONS]: { maxWidth: 'sm' },
    [MODALS.SOURCE.RAW_TABLE_DATA]: { maxWidth: 'xl' },
    [MODALS.SOURCE.RENAME_TABLE]: { maxWidth: 'sm' },
    [MODALS.SOURCE.PROPERTIES]: { maxWidth: 'lg' },
    [MODALS.COMMON.TEXT_INPUT]: { maxWidth: 'sm' },
    [MODALS.COMMON.REQUEST_INTEGRATION]: { maxWidth: 'sm' },
    [MODALS.CANVAS.ADD_VIDEO]: { maxWidth: 'md' },
    [MODALS.INSIGHTS.SAVE_ML_MODEL]: { maxWidth: 'sm' },
    [MODALS.CHAT.FEEDBACK]: { maxWidth: 'sm' },
    [MODALS.ACCOUNT.INVITE_MEMBER]: { maxWidth: 'md' },
    [MODALS.PROCESS.LOAD_DIAGRAM]: { maxWidth: 'md' },
    [MODALS.PROCESS.FILTER_SELECTION]: { maxWidth: 'md' },
    [MODALS.LIVE_WORKSHEETS.CREATE]: { maxWidth: 'md' },
    [MODALS.ONBOARDING.QUESTION]: { maxWidth: 'md' },
    [MODALS.ONBOARDING.COMPLETE]: { maxWidth: 'sm' },
}
